import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardBody, Col, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { Modulo } from '../../../Types/Tables.type';
import { get, postJson, putJson } from '../../../hook/request';
import CompAbilitato from './radioAbilitato';
import { Btn } from '../../../AbstractElements';
import { buildTreeOptions } from '../../../Helpers';
import { Link } from 'react-router-dom';
import Loader from '../../../Components/Widgets/Loader/generic';


type FormState = {
  codice: string;
  classe: string;
  parentID: string; 
  nome: string;
  permalink: string;
  percorso_file: string;
  ordine: number | string;
  abilitato: 1 | 0;
};

type ApiDettaglio = {
  codice?: string;
  classe?: string;
  parent_id?: string | number | null;
  nome?: string;
  nome_it?: string;
  permalink?: string;
  permalink_it?: string;
  percorso_file?: string;
  pathfile?: string;
  ordine?: number | string;
  abilitato?: boolean | 0 | 1 | '0' | '1';
};


type Lang = {
  ID: number | string;
  nome: string;
  classe: string;      
  codice: string;      
  abilitato: 0 | 1;
};

type Translations = Record<string, { nome: string; permalink: string }>;


export default function ModuloForm() {
  const { slug } = useParams<{ slug: string }>();
  const isEdit = useMemo(() => !!slug && slug !== 'nuovo', [slug]);

  const [parentList, setParentList] = useState<Modulo[]>([]);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const busy = loading || parentList.length === 0; 

  const [languages, setLanguages] = useState<Lang[]>([]);
  const [activeLang, setActiveLang] = useState<string>('it');   
  const [translations, setTranslations] = useState<Translations>({});

  const [form, setForm] = useState<FormState>({
    codice: '',
    classe: '',
    parentID: '',
    nome: '',
    permalink: '',
    percorso_file: '',
    ordine: '',
    abilitato: 1,
  });


  useEffect(() => {
  (async () => {
    try {
      const res = await get<any>('/configurazione_menu/list');
      const raw = (res as any)?.data ?? res;

      let normalized: any[] = [];

      if (Array.isArray(raw)) {
        normalized = raw.map(normalizeMenuNode);
      } else if (raw && typeof raw === 'object') {
        normalized = [normalizeMenuNode(raw)];
      }
      setParentList(normalized as any);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento della lista parent');
    }
  })();
}, []);



  // Carico dettaglio se in edit
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!isEdit) return;
      try {
        setLoading(true);
        const res = await get<ApiDettaglio & { meta?: unknown }>(`/configurazione_menu/${encodeURIComponent(String(slug))}`);
        const data = (res as any)?.data ?? (res as any);
        if (!mounted) return;

        setForm({
          codice: data?.codice ?? '',
          classe: data?.classe ?? '',
          parentID: data?.parentID == null || Number(data?.parentID) === 0 ? '' : String(data?.parentID),
          nome: data?.nome ?? data?.nome_it ?? '',
          permalink: data?.permalink_it ?? data?.permalink ?? '',
          percorso_file: data?.pathfile ?? data?.percorso_file ?? '',
          ordine: data?.ordine ?? '',
          abilitato: data?.abilitato === 1 || data?.abilitato === '1' || data?.abilitato === true ? 1 : 0,
        });

        const meta = parseMeta((data as any)?.meta);
        const enabledCodes = languages.length
          ? languages.filter(l => l.abilitato === 1).map(l => (l.codice || '').toLowerCase())
          : Object.keys(meta); // fallback: usa tutte le chiavi presenti in meta

        const next: Translations = {};
        for (const code of enabledCodes) {
          const byLang = meta[code] || meta[code.toLowerCase()] || meta[code.toUpperCase()] || {};
          next[code.toLowerCase()] = {
            nome: (byLang.nome ?? '').toString(),
            permalink: (byLang.permalink ?? '').toString(), // se non c’è va stringa vuota
          };
        }
        setTranslations(prev => ({ ...prev, ...next }));
        if (enabledCodes.length) setActiveLang(enabledCodes[0].toLowerCase());

      } catch (err: any) {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Errore nel caricamento';
        toast.error(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
    // 👇 dipende anche dalle languages, così quando arrivano puoi mappare meta sulle lingue abilitate
  }, [isEdit, slug, languages]);



  useEffect(() => {
    (async () => {
      try {
        const res = await get<unknown>('/languages');
        const raw = (res as any)?.data ?? res;
        const list: Lang[] = Array.isArray(raw) ? raw as Lang[] : [];
        setLanguages(list);

        const codes = list.filter(l => l.abilitato === 1).map(l => (l.codice || '').toLowerCase());
        const next: Translations = {};
        for (const c of codes) next[c] = { nome: '', permalink: '' };
        setTranslations(prev => ({ ...next, ...prev }));

        if (codes.length && !prevHasAny(translations)) setActiveLang(codes[0] || 'it');
      } catch (e) {
        toast.error('Errore nel caricamento lingue');
      }
    })();
  }, []);




  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const enabledCodes = languages.filter(l => l.abilitato === 1).map(l => (l.codice || '').toLowerCase());
    const namesPayload = Object.fromEntries(
      enabledCodes.map(c => [`nome_${c}`, translations[c]?.nome ?? ''])
    );
    const permalinksPayload = Object.fromEntries(
      enabledCodes.map(c => [`permalink_${c}`, translations[c]?.permalink ?? ''])
    );

    const user_email = localStorage.getItem('email');
    const payload = {
      id: slug,
      codice: form.codice,
      classe: form.classe,
      parentID: form.parentID === '' ? null : Number(form.parentID),
      percorso_file: form.percorso_file,
      ordine: Number(form.ordine || 0),
      abilitato: form.abilitato === 1 ? 1 : 0,
      user_email,
      ...namesPayload,       
      ...permalinksPayload,   
    };


    try {
      if (isEdit) {
        await putJson(`/configurazione_menu/${encodeURIComponent(String(slug))}`, payload);
        toast.success('Configurazione aggiornata!');
      } else {
        await postJson('/configurazione_menu/insert', payload);
        toast.success('Configurazione salvata!');
        setForm({
          codice: '',
          classe: '',
          parentID: '',
          nome: '',
          permalink: '',
          percorso_file: '',
          ordine: '',
          abilitato: 1,
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Errore di rete';
      toast.error(`Errore salvataggio: ${msg}`);
    }
  };


  const treeOptions = useMemo(
  () => makeTreeOptions(parentList as any, '', isEdit ? String(slug) : undefined),
  [parentList, isEdit, slug]
);


  // ---- UI ----

  if (busy) {
    return (
      <div className="page-body pt-4">
        <Col xl={12} className="mb-lg-3 d-flex justify-content-end">
          <Link to="/admin/moduli">
            <Btn className="px-4" color="primary" type="button">
              Torna Indietro
            </Btn>
          </Link>
        </Col>

        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody className="d-flex justify-content-center">
              <Loader /> {/* ← qui il tuo componente loader */}
            </CardBody>
          </Card>
        </Col>
      </div>
    );
  }


  return (
    <div className="page-body pt-4">
      <Col xl={12} className="mb-lg-3 d-flex justify-content-end">
        <Link to="/admin/moduli">
          <Btn className="px-4" color="primary" type="button">
            Torna Indietro
          </Btn>
        </Link>
      </Col>
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col xs={6}>
                  <Label htmlFor="codice">Codice</Label>
                  <Input
                    id="codice"
                    type="text"
                    placeholder="codice"
                    name="codice"
                    value={form.codice}
                    onChange={onChange}
                    required
                    disabled={loading}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="classe">Classe</Label>
                  <Input
                    id="classe"
                    type="text"
                    placeholder="classe"
                    name="classe"
                    value={form.classe}
                    onChange={onChange}
    
                    disabled={loading}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="parentID">ParentID</Label>
                  <Input
                    type="select"
                    id="parentID"
                    name="parentID"
                    value={form.parentID}
                    onChange={onChange}
                    disabled={loading}
                  >
                    <option value="">{'Scegli...'}</option>
                    {treeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col xs={6}>
                  <Label htmlFor="percorso-file">Percorso file</Label>
                  <Input
                    id="percorso-file"
                    type="text"
                    placeholder="percorso file"
                    name="percorso_file"
                    value={form.percorso_file}
                    onChange={onChange}
                    disabled={loading}
                  />
                </Col>

                <Col xs={6}>
                  <Label htmlFor="ordine">Ordine</Label>
                  <Input
                    id="ordine"
                    type="number"
                    placeholder="ordine"
                    name="ordine"
                    value={form.ordine}
                    onChange={onChange}
                    required
                    disabled={loading}
                  />
                </Col>

                {/* Tabs lingue */}
                <div className="mb-3">
                  <ul className="nav nav-tabs">
                    {languages.filter(l => l.abilitato === 1).map(l => {
                      const code = (l.codice || '').toUpperCase();
                      const codeLower = code.toLowerCase();
                      return (
                        <li className="nav-item" key={code}>
                          <a
                            href="#"
                            className={`nav-link ${activeLang.toUpperCase() === code ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); setActiveLang(codeLower); }}
                          >
                            {code}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Campi Nome/Permalink per lingua attiva */}
                <Row className="g-3">
                  <Col xs={6}>
                    <Label htmlFor={`nome-${activeLang}`}>Nome {activeLang.toUpperCase()}</Label>
                    <Input
                      id={`nome-${activeLang}`}
                      type="text"
                      placeholder={`nome ${activeLang.toUpperCase()}`}
                      value={translations[activeLang]?.nome ?? ''}
                      onChange={(e) =>
                        setTranslations(prev => ({
                          ...prev,
                          [activeLang]: { ...(prev[activeLang] || { nome: '', permalink: '' }), nome: e.target.value },
                        }))
                      }
                      disabled={loading}
                      required
                    />
                  </Col>

                  <Col xs={6}>
                    <Label htmlFor={`permalink-${activeLang}`}>Permalink {activeLang.toUpperCase()}</Label>
                    <Input
                      id={`permalink-${activeLang}`}
                      type="text"
                      placeholder={`permalink ${activeLang.toUpperCase()}`}
                      value={translations[activeLang]?.permalink ?? ''}
                      onChange={(e) =>
                        setTranslations(prev => ({
                          ...prev,
                          [activeLang]: { ...(prev[activeLang] || { nome: '', permalink: '' }), permalink: e.target.value },
                        }))
                      }
                      disabled={loading}
                      
                    />
                  </Col>
                </Row>


                <Col xs={6}>
                  <CompAbilitato
                    value={form.abilitato}
                    onChange={(v) => setForm((p) => ({ ...p, abilitato: v }))}
                  />
                </Col>

                <Col xs={12} className="mt-3 d-flex align-items-center gap-2">
                  <Btn className="px-4" color="primary" type="submit" disabled={loading}>
                    {isEdit ? 'Aggiorna' : 'Crea'}
                  </Btn>
                  {loading && <Spinner size="sm" />}
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}



function prevHasAny(tr: Translations) {
  return Object.keys(tr).length > 0;
}

type MetaI18n = Record<string, { nome?: string; permalink?: string }>;

function parseMeta(meta: unknown): MetaI18n {
  if (!meta) return {};
  try {
    if (typeof meta === 'string') return JSON.parse(meta) as MetaI18n;
    if (typeof meta === 'object')  return meta as MetaI18n;
    return {};
  } catch {
    return {};
  }
}


function normalizeMenuNode(node: any): any {
  const meta = parseMeta(node.meta);
  const itMeta = meta?.it || {};

  return {
    ...node,
    nome: itMeta.nome || node.codice || String(node.ID),
    children: Array.isArray(node.children)
      ? node.children.map(normalizeMenuNode)
      : [],
  };
}


type TreeOption = { value: string; label: string };

function makeTreeOptions(
  nodes: any[],
  indent = '',
  excludeId?: string
): TreeOption[] {
  const out: TreeOption[] = [];

  for (const n of nodes) {
    if (excludeId && String(n.ID) === String(excludeId)) {
      if (Array.isArray(n.children) && n.children.length) {
        out.push(...makeTreeOptions(n.children, indent + '    ', excludeId));
      }
      continue;
    }
    const label = indent + (n.nome || n.codice || String(n.ID));
    out.push({
      value: String(n.ID),
      label,
    });

    if (Array.isArray(n.children) && n.children.length) {
      out.push(...makeTreeOptions(n.children, indent + '    ', excludeId));
    }
  }

  return out;
}


